import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { EdgeLocation } from '@cast/types-omni';

import { apiClient } from '@cast/infrastructure-api';
import { EnhancedEdgeLocation } from 'features/cluster/autoscaler/edge-locations/types';
import { getEdgeLocationMappedState } from 'features/cluster/autoscaler/edge-locations/utils';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { isNotFoundError } from 'utils/api-utils';

import { getClusterEdgeLocationsQueryKey } from './useGetClusterEdgeLocationsQuery';

const OMNI_EDGE_LOCATION_DETAILS_QUERY_KEY = 'omni-edge-location-details';

const getEdgeLocationDetailsQueryKey = (
  clusterId: string,
  edgeLocationId: string
) => [OMNI_EDGE_LOCATION_DETAILS_QUERY_KEY, clusterId, edgeLocationId] as const;

const getEdgeLocationDetailsQueryOptions = ({
  clusterId,
  edgeLocationId,
  organizationId,
  refetchInterval,
  enabled,
  queryClient,
}: {
  clusterId: string;
  edgeLocationId: string;
  organizationId: string | undefined;
  refetchInterval?: QueryRefetchInterval<EdgeLocation | null>;
  enabled: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
}) =>
  queryOptions({
    queryKey: getEdgeLocationDetailsQueryKey(clusterId, edgeLocationId),
    queryFn: async () => {
      const { data } = await apiClient.omni
        .getEdgeLocationDetails({
          organizationId: organizationId!,
          clusterId,
          id: edgeLocationId,
        })
        .catch((error) => {
          // Edge location is deleted during offboarding so request will return 404
          if (isNotFoundError(error)) {
            return { data: null };
          }

          throw error;
        });

      // Update edge location in table
      queryClient.setQueriesData(
        { queryKey: getClusterEdgeLocationsQueryKey({ clusterId }) },
        (oldData: EnhancedEdgeLocation[]): EnhancedEdgeLocation[] => {
          if (!data) {
            return oldData?.filter(
              (edgeLocation) => edgeLocation.id !== edgeLocationId
            );
          }

          return oldData?.map((edgeLocation) => {
            if (edgeLocation.id === edgeLocationId) {
              return {
                ...edgeLocation,
                state: data.state,
                mappedState: getEdgeLocationMappedState(
                  data,
                  edgeLocation.templateUsage
                ),
              };
            }

            return edgeLocation;
          });
        }
      );

      return data;
    },
    enabled: enabled && !!clusterId && !!edgeLocationId,
    refetchInterval,
  });

export const useGetEdgeLocationDetailsQuery = ({
  clusterId,
  edgeLocationId,
  refetchInterval,
  enabled = true,
}: {
  clusterId: string;
  edgeLocationId: string;
  refetchInterval?: QueryRefetchInterval<EdgeLocation | null>;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizations();

  const {
    data: edgeLocationDetails,
    isLoading,
    error,
    refetch,
  } = useQuery(
    getEdgeLocationDetailsQueryOptions({
      clusterId,
      edgeLocationId,
      organizationId: currentOrganization?.id,
      refetchInterval,
      enabled,
      queryClient,
    })
  );

  return {
    edgeLocationDetails,
    isLoading,
    error,
    refetch,
  };
};
